import { useState, useEffect, useCallback } from 'react'
import { api } from '../context/AuthContext'
import { API_FILE_URL } from '../constant/api'

// ── Helpers ───────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || API_FILE_URL

const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'
const num     = (n) => n != null ? Number(n).toLocaleString() : '0'

const avatarBg = (str = '') => {
  const colors = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return colors[Math.abs(h) % colors.length]
}
const initials = (name = '') => (name.trim().charAt(0) || '?').toUpperCase()

const parseThumb = (thumbnail) => {
  if (!thumbnail) return null
  try {
    const arr = typeof thumbnail === 'string' && thumbnail.startsWith('[') ? JSON.parse(thumbnail) : null
    const raw = arr ? arr[0] : thumbnail
    if (!raw) return null
    const url = String(raw).trim()
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    return `${API_BASE}/${url.replace(/\\/g, '/').split('/').pop()}`
  } catch { return null }
}

const REPORT_TYPE = {
  comment: { label: '💬 Comment', color: '#0369a1', bg: '#f0f9ff' },
  reel:    { label: '▶ Clip',     color: '#b45309', bg: '#fffbeb' },
  stream:  { label: '🔴 Stream',  color: '#b91c1c', bg: '#fef2f2' },
  video:   { label: '🎬 Video',   color: '#0d9488', bg: '#f0fdfa' },
}

const COMMENT_TYPE_LABELS = {
  reel:    { label: '▶ Clip',    color: '#b45309', bg: '#fffbeb' },
  product: { label: '🛍 Product', color: '#6d28d9', bg: '#f5f3ff' },
  driver:  { label: '🚚 Driver',  color: '#0369a1', bg: '#f0f9ff' },
  live:    { label: '🔴 Stream',  color: '#b91c1c', bg: '#fef2f2' },
}

// ── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

function ActionBtn({ onClick, color, children }) {
  return (
    <button
      onClick={onClick}
      style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${color}30`, background: `${color}12`, color, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", whiteSpace: 'nowrap' }}
    >{children}</button>
  )
}

function EmptyState({ message }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
      <div style={{ fontSize: 15, fontWeight: 700 }}>All clear</div>
      <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>{message}</div>
    </div>
  )
}

function Pagination({ page, pages, onPrev, onNext }) {
  if (pages <= 1) return null
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
      <button onClick={onPrev} disabled={page === 1} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>← Prev</button>
      <span style={{ fontSize: 12, color: '#9a9a94', alignSelf: 'center', fontFamily: "'JetBrains Mono',monospace" }}>{page} / {pages}</span>
      <button onClick={onNext} disabled={page === pages} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: page === pages ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700 }}>Next →</button>
    </div>
  )
}

// ── User Report Card ──────────────────────────────────────────────────────────
function ReportCard({ item, onAction }) {
  const meta     = REPORT_TYPE[item.type] || { label: item.type, color: '#6b7280', bg: '#f3f4f6' }
  const count    = parseInt(item.report_count) || 1
  const isHidden = item.type === 'video' ? item.content_status === 'rejected' : parseInt(item.content_status) === 0
  const hasUser  = !!item.creator_user_id

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(239,68,68,0.25)', borderLeft: `3px solid ${count >= 3 ? '#ef4444' : '#f59e0b'}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>

        {/* Report count badge */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0, minWidth: 52 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: meta.bg, color: meta.color, fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{meta.label}</span>
          <span style={{ fontSize: 18, fontWeight: 900, color: count >= 3 ? '#ef4444' : '#f59e0b', fontFamily: "'JetBrains Mono',monospace" }}>{count}</span>
          <span style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>report{count !== 1 ? 's' : ''}</span>
        </div>

        {/* Body */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
            {item.creator_name
              ? <span style={{ fontSize: 13, fontWeight: 700 }}>{item.creator_name}</span>
              : <span style={{ fontSize: 12, color: '#9a9a94' }}>Unknown user</span>
            }
            <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{item.target_id}</span>
            {isHidden && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: '#fef2f2', color: '#b91c1c', fontFamily: "'JetBrains Mono',monospace" }}>HIDDEN</span>}
            {item.is_live && <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: '#fef2f2', color: '#ef4444', fontFamily: "'JetBrains Mono',monospace" }}>● LIVE</span>}
            <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginLeft: 'auto' }}>{fmtTime(item.latest_report)}</span>
          </div>

          {/* Content preview */}
          {item.content_preview && (
            <div style={{ fontSize: 13, color: '#374151', background: '#f9f9f7', borderRadius: 6, padding: '8px 10px', marginBottom: 8, lineHeight: 1.55, wordBreak: 'break-word', border: '1px solid #e9e9e4' }}>
              {item.type === 'comment' ? `"${item.content_preview}"` : item.content_preview}
            </div>
          )}

          {/* Reasons */}
          <div style={{ fontSize: 11, color: '#b45309', fontFamily: "'JetBrains Mono',monospace", marginBottom: 10 }}>
            Reason{item.reasons?.includes(' · ') ? 's' : ''}: {item.reasons || '—'}
          </div>

          {/* Action row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Always available */}
            <ActionBtn onClick={() => onAction('dismiss', item)} color="#6b7280">✓ Dismiss</ActionBtn>

            {/* Suspend account */}
            {hasUser && (
              <ActionBtn onClick={() => onAction('suspend', item)} color="#7c3aed">🚫 Suspend Account</ActionBtn>
            )}

            {/* Content-specific actions */}
            {item.type === 'comment' && (
              <ActionBtn onClick={() => onAction('deleteContent', item)} color="#b91c1c">🗑 Delete Comment</ActionBtn>
            )}
            {item.type === 'reel' && !isHidden && (
              <ActionBtn onClick={() => onAction('hideClip', item)} color="#d97706">⊘ Hide Clip</ActionBtn>
            )}
            {item.type === 'reel' && isHidden && (
              <ActionBtn onClick={() => onAction('hideClip', item)} color="#15803d">↩ Restore Clip</ActionBtn>
            )}
            {item.type === 'reel' && (
              <ActionBtn onClick={() => onAction('deleteContent', item)} color="#b91c1c">🗑 Delete Clip</ActionBtn>
            )}
            {item.type === 'video' && !isHidden && (
              <ActionBtn onClick={() => onAction('hideClip', item)} color="#d97706">⊘ Take Down Video</ActionBtn>
            )}
            {item.type === 'video' && isHidden && (
              <ActionBtn onClick={() => onAction('hideClip', item)} color="#15803d">↩ Restore Video</ActionBtn>
            )}
            {item.type === 'video' && (
              <ActionBtn onClick={() => onAction('deleteContent', item)} color="#b91c1c">🗑 Delete Video</ActionBtn>
            )}
            {item.type === 'stream' && item.is_live && (
              <ActionBtn onClick={() => onAction('endStream', item)} color="#b91c1c">⏹ End Stream</ActionBtn>
            )}

            {/* Nuclear option — only show if user is known */}
            {hasUser && ['comment', 'reel', 'video'].includes(item.type) && (
              <ActionBtn onClick={() => onAction('deleteAndSuspend', item)} color="#991b1b">💀 Delete & Suspend</ActionBtn>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Admin-flagged Comment Card ────────────────────────────────────────────────
function CommentCard({ item, onUnflag, onDelete }) {
  const meta = COMMENT_TYPE_LABELS[item.type] || { label: item.type, color: '#6b7280', bg: '#f3f4f6' }
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(239,68,68,0.3)', borderLeft: '3px solid #ef4444', borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, background: avatarBg(item.username || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0 }}>
        {initials(item.username || 'A')}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>{item.username || 'Anonymous'}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: meta.bg, color: meta.color, fontFamily: "'JetBrains Mono',monospace" }}>{meta.label}</span>
          {item.rating && <span style={{ fontSize: 10, color: '#d97706', fontFamily: "'JetBrains Mono',monospace" }}>★ {item.rating}</span>}
          <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginLeft: 'auto' }}>{fmtTime(item.created_at)}</span>
        </div>
        <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, marginBottom: 6, wordBreak: 'break-word' }}>{item.content}</div>
        {item.reference && (
          <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>on: {item.reference}</div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionBtn onClick={() => onUnflag(item)} color="#15803d">✓ Unflag</ActionBtn>
          <ActionBtn onClick={() => onDelete(item)} color="#b91c1c">🗑 Delete</ActionBtn>
        </div>
      </div>
    </div>
  )
}

// ── Clip Card ─────────────────────────────────────────────────────────────────
function ClipCard({ item, onHide, onDelete }) {
  const thumb = parseThumb(item.thumbnail_url || item.thumbnail || item.video_url)
  const isHidden = item.status === 0
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(239,68,68,0.3)', borderLeft: '3px solid #f59e0b', borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 12 }}>
      <div style={{ width: 52, height: 70, borderRadius: 8, background: '#111317', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
        {thumb
          ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: 'rgba(255,255,255,0.4)' }}>▶</div>
        }
        {isHidden && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', fontFamily: "'JetBrains Mono',monospace" }}>HIDDEN</span>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title || `Clip #${item.reel_id}`}</div>
          <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono',monospace", color: '#9a9a94', whiteSpace: 'nowrap' }}>{fmtTime(item.created_at)}</span>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>by {item.creator_name || item.creator_phone || '—'}{item.shop_name ? ` · ${item.shop_name}` : ''}</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>👁 {num(item.views)}</span>
          <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>❤️ {num(item.likes)}</span>
          {item.report_count > 0 && <span style={{ fontSize: 11, color: '#b91c1c', fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>⚑ {item.report_count} report{item.report_count !== 1 ? 's' : ''}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionBtn onClick={() => onHide(item)} color={isHidden ? '#15803d' : '#d97706'}>{isHidden ? '✓ Restore' : '⊘ Hide'}</ActionBtn>
          <ActionBtn onClick={() => onDelete(item)} color="#b91c1c">🗑 Delete</ActionBtn>
        </div>
      </div>
    </div>
  )
}

// ── Video Card — mirrors ClipCard, adapted to long-form videos: no shop_name
// (videos are always creator-owned), title falls back to Video #id. ──
function VideoCard({ item, onHide, onDelete }) {
  const thumb = parseThumb(item.thumbnail_url)
  const isHidden = item.status === 'rejected'
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(239,68,68,0.3)', borderLeft: '3px solid #f59e0b', borderRadius: 10, padding: '14px 16px', display: 'flex', gap: 12 }}>
      <div style={{ width: 52, height: 70, borderRadius: 8, background: '#111317', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
        {thumb
          ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display='none'} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: 'rgba(255,255,255,0.4)' }}>🎬</div>
        }
        {isHidden && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', fontFamily: "'JetBrains Mono',monospace" }}>TAKEN DOWN</span>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title || `Video #${item.video_id}`}</div>
          <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono',monospace", color: '#9a9a94', whiteSpace: 'nowrap' }}>{fmtTime(item.created_at)}</span>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>by {item.creator_name || item.creator_username || item.creator_phone || '—'}</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>👁 {num(item.views_count)}</span>
          <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>❤️ {num(item.likes)}</span>
          {item.report_count > 0 && <span style={{ fontSize: 11, color: '#b91c1c', fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>⚑ {item.report_count} report{item.report_count !== 1 ? 's' : ''}</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionBtn onClick={() => onHide(item)} color={isHidden ? '#15803d' : '#d97706'}>{isHidden ? '✓ Restore' : '⊘ Take Down'}</ActionBtn>
          <ActionBtn onClick={() => onDelete(item)} color="#b91c1c">🗑 Delete</ActionBtn>
        </div>
      </div>
    </div>
  )
}

// ── Clip Preview Modal ────────────────────────────────────────────────────────
function ClipPreviewModal({ open, item, pendingAction, onConfirm, onClose, loading }) {
  if (!open || !item) return null
  const isVideo  = item.type === 'video'
  const label    = isVideo ? 'Video' : 'Clip'
  const videoUrl = item.video_url || null
  const thumbUrl = parseThumb(item.thumbnail_url || item.thumbnail)
  const isHidden = isVideo ? (item.content_status ?? item.status) === 'rejected' : parseInt(item.content_status ?? item.status) === 0
  const title    = item.caption || item.title || item.content_preview || `${label} #${item.reel_id || item.target_id || item.video_id}`
  const creator  = item.creator_name || item.creator_phone || '—'

  const ACTION_META = {
    hide:            isHidden ? { label: `✓ Restore ${label}`,   color: '#15803d' } : { label: `⊘ Hide ${label}`,      color: '#d97706' },
    hideClip:        isHidden ? { label: `✓ Restore ${label}`,   color: '#15803d' } : { label: isVideo ? `⊘ Take Down ${label}` : `⊘ Hide ${label}`, color: '#d97706' },
    delete:          { label: `🗑 Delete ${label}`,          color: '#b91c1c' },
    deleteContent:   { label: `🗑 Delete ${label}`,          color: '#b91c1c' },
    suspend:         { label: '🚫 Suspend Account',      color: '#7c3aed' },
    deleteAndSuspend:{ label: '💀 Delete & Suspend',     color: '#991b1b' },
  }
  const meta = ACTION_META[pendingAction] || { label: 'Confirm', color: '#e8440a' }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 500 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, background: '#111317', borderRadius: 16, zIndex: 501, boxShadow: '0 24px 80px rgba(0,0,0,0.5)', overflow: 'hidden', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        {/* Video / thumbnail */}
        <div style={{ width: '100%', height: 280, background: '#000', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {videoUrl
            ? <video src={videoUrl} controls poster={thumbUrl || undefined} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            : thumbUrl
              ? <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => e.target.style.display = 'none'} />
              : <div style={{ fontSize: 48, color: 'rgba(255,255,255,0.25)' }}>▶</div>
          }
          {isHidden && (
            <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.75)', borderRadius: 4, padding: '3px 10px', fontSize: 10, fontWeight: 700, color: '#fff', fontFamily: "'JetBrains Mono',monospace" }}>HIDDEN</div>
          )}
        </div>
        {/* Info + actions */}
        <div style={{ padding: '14px 18px 18px' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
          <div style={{ fontSize: 12, color: '#9a9a94', marginBottom: 14 }}>by {creator}{item.shop_name ? ` · ${item.shop_name}` : ''}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={loading} style={{ flex: 1, height: 40, borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#e5e7eb', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
            <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 40, borderRadius: 8, border: 'none', background: loading ? '#555' : meta.color, color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {loading ? 'Working…' : meta.label}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ open, title, message, confirmLabel, onConfirm, onClose, loading, danger }) {
  if (!open) return null
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 400 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 380, background: '#fff', borderRadius: 14, zIndex: 401, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>{danger ? '🗑️' : '✓'}</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>{title}</div>
          <div style={{ fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>{message}</div>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#ccc' : (danger ? '#e8440a' : '#15803d'), color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Working…' : (confirmLabel || 'Confirm')}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Flagged() {
  const [tab, setTab] = useState('reports') // 'reports' | 'flagged' | 'clips'

  // User reports state (from reports table)
  const [reports,     setReports]    = useState([])
  const [repLoad,     setRepLoad]    = useState(true)
  const [repTotal,    setRepTotal]   = useState(0)
  const [repPage,     setRepPage]    = useState(1)
  const [repPages,    setRepPages]   = useState(1)
  const [repStats,    setRepStats]   = useState({ reel: 0, comment: 0, stream: 0, video: 0, total: 0 })

  // Admin-flagged comments state
  const [comments,    setComments]   = useState([])
  const [commLoad,    setCommLoad]   = useState(false)
  const [commTotal,   setCommTotal]  = useState(0)
  const [commPage,    setCommPage]   = useState(1)
  const [commPages,   setCommPages]  = useState(1)

  // Reported clips state
  const [clips,       setClips]      = useState([])
  const [clipLoad,    setClipLoad]   = useState(false)
  const [clipTotal,   setClipTotal]  = useState(0)
  const [clipPage,    setClipPage]   = useState(1)
  const [clipPages,   setClipPages]  = useState(1)

  // Reported videos state
  const [videos,      setVideos]     = useState([])
  const [vidLoad,     setVidLoad]    = useState(false)
  const [vidTotal,    setVidTotal]   = useState(0)
  const [vidPage,     setVidPage]    = useState(1)
  const [vidPages,    setVidPages]   = useState(1)

  // Confirm modal
  const [modal, setModal] = useState(null)  // { kind, item, action }
  const [modalLoad, setModalLoad] = useState(false)

  // Clip preview modal
  const [clipPreview, setClipPreview] = useState(null)  // { item, pendingAction }
  const [previewLoad, setPreviewLoad] = useState(false)

  const PER_PAGE = 20

  // ── Fetch user reports ──
  const fetchReports = useCallback(async () => {
    setRepLoad(true)
    try {
      const params = new URLSearchParams({ page: repPage, limit: PER_PAGE })
      const res = await api.get(`/comments/admin/reports?${params}`)
      setReports(res.data.reports   || [])
      setRepPages(res.data.totalPages || 1)
      setRepTotal(res.data.total      || 0)
      setRepStats(res.data.stats      || { reel: 0, comment: 0, stream: 0, video: 0, total: 0 })
    } catch {
      setReports(Array.from({ length: 4 }, (_, i) => ({
        type:            ['comment','reel','stream','video'][i % 4],
        target_id:       i + 1,
        report_count:    i + 1,
        reasons:         'Scam or fraud',
        content_preview: i === 0 ? 'This product is fake and the seller is scamming people!' : null,
        creator_name:    `user_${i + 1}`,
        latest_report:   new Date(Date.now() - i * 3600000 * 6).toISOString(),
      })))
      setRepPages(1); setRepTotal(4)
      setRepStats({ reel: 1, comment: 1, stream: 1, video: 1, total: 4 })
    } finally { setRepLoad(false) }
  }, [repPage])

  // ── Fetch admin-flagged comments (lazy — only when tab opened) ──
  const fetchComments = useCallback(async () => {
    setCommLoad(true)
    try {
      const params = new URLSearchParams({ page: commPage, limit: PER_PAGE, flagged: '1' })
      const res = await api.get(`/comments/admin/all?${params}`)
      setComments(res.data.items       || [])
      setCommPages(res.data.totalPages || 1)
      setCommTotal(res.data.total      || 0)
    } catch {
      setComments([])
      setCommPages(1); setCommTotal(0)
    } finally { setCommLoad(false) }
  }, [commPage])

  // ── Fetch reported clips (lazy) ──
  const fetchClips = useCallback(async () => {
    setClipLoad(true)
    try {
      const params = new URLSearchParams({ page: clipPage, limit: PER_PAGE, reported: '1' })
      const res = await api.get(`/reels/admin/reels?${params}`)
      setClips(res.data.reels       || [])
      setClipPages(res.data.totalPages || 1)
      setClipTotal(res.data.total      || 0)
    } catch {
      setClips([]); setClipPages(1); setClipTotal(0)
    } finally { setClipLoad(false) }
  }, [clipPage])

  // ── Fetch reported videos (lazy) ──
  const fetchVideos = useCallback(async () => {
    setVidLoad(true)
    try {
      const params = new URLSearchParams({ page: vidPage, limit: PER_PAGE })
      const res = await api.get(`/videos/admin/reported?${params}`)
      setVideos((res.data.videos || []).map(v => ({ ...v, type: 'video' })))
      setVidPages(res.data.totalPages || 1)
      setVidTotal(res.data.total      || 0)
    } catch {
      setVideos([]); setVidPages(1); setVidTotal(0)
    } finally { setVidLoad(false) }
  }, [vidPage])

  useEffect(() => { fetchReports() }, [fetchReports])
  useEffect(() => { if (tab === 'flagged') fetchComments() }, [tab, fetchComments])
  useEffect(() => { if (tab === 'clips')   fetchClips()    }, [tab, fetchClips])
  useEffect(() => { if (tab === 'videos')  fetchVideos()   }, [tab, fetchVideos])
  useEffect(() => { setRepPage(1)  }, [])
  useEffect(() => { setCommPage(1) }, [tab])
  useEffect(() => { setClipPage(1) }, [tab])
  useEffect(() => { setVidPage(1)  }, [tab])

  // ── Remove a report row from the list ──
  const removeReport = (item) => {
    setReports(prev => prev.filter(r => !(r.type === item.type && String(r.target_id) === String(item.target_id))))
    setRepTotal(t => Math.max(0, t - 1))
  }

  // ── Single dispatcher for all report-tab actions ──
  const handleReportAction = (action, item) => {
    // Clip/video-related actions always show the video preview first
    if (['reel', 'video'].includes(item.type) && ['hideClip', 'deleteContent', 'deleteAndSuspend', 'suspend'].includes(action)) {
      setClipPreview({ item, pendingAction: action })
      return
    }

    const name = item.creator_name || 'this user'
    const label = item.type === 'comment' ? 'Comment' : item.type === 'reel' ? 'Clip' : item.type === 'video' ? 'Video' : 'Stream'
    const isHidden = item.type === 'video' ? item.content_status === 'rejected' : parseInt(item.content_status) === 0

    const CONFIGS = {
      dismiss: {
        title: 'Dismiss Reports?',
        message: `Remove all ${item.report_count} report(s) against this ${item.type}. The content stays untouched.`,
        confirmLabel: 'Yes, Dismiss', danger: false,
      },
      suspend: {
        title: `Suspend @${name}?`,
        message: `${name}'s account will be deactivated. They will no longer be able to log in. You can reactivate them from User Management.`,
        confirmLabel: 'Suspend Account', danger: true,
      },
      deleteContent: {
        title: `Delete ${label}?`,
        message: `This ${label.toLowerCase()} will be permanently deleted and all reports against it dismissed.`,
        confirmLabel: `Delete ${label}`, danger: true,
      },
      hideClip: {
        title: isHidden ? `Restore ${label}?` : `Take Down ${label}?`,
        message: isHidden
          ? `The ${label.toLowerCase()} will be made visible again.`
          : `The ${label.toLowerCase()} will be hidden from all users. Reports stay open until you dismiss them.`,
        confirmLabel: isHidden ? 'Restore' : `Take Down ${label}`, danger: !isHidden,
      },
      endStream: {
        title: 'Force End Stream?',
        message: `${name}'s live stream will be immediately terminated and all reports dismissed.`,
        confirmLabel: 'End Stream', danger: true,
      },
      deleteAndSuspend: {
        title: `Delete ${label} & Suspend @${name}?`,
        message: `The ${label.toLowerCase()} will be deleted AND ${name}'s account will be suspended. This cannot be undone.`,
        confirmLabel: 'Delete & Suspend', danger: true,
      },
    }

    const cfg = CONFIGS[action]
    if (!cfg) return
    setModal({ kind: action, item, ...cfg })
  }

  // ── Confirm modal handler ──
  const confirmModal = async () => {
    if (!modal) return
    setModalLoad(true)
    try {
      const { kind, item } = modal

      // Helper: dismiss associated reports silently
      const dismissReports = () =>
        api.delete(`/comments/admin/reports/${item.type}/${item.target_id}`).catch(() => {})
      // Helper: suspend creator account silently
      const suspendUser = () =>
        item.creator_user_id
          ? api.patch(`/user/admin/users/${item.creator_user_id}/status`, { status: 0 }).catch(() => {})
          : Promise.resolve()

      if (kind === 'dismiss') {
        await api.delete(`/comments/admin/reports/${item.type}/${item.target_id}`)
        removeReport(item)

      } else if (kind === 'suspend') {
        await api.patch(`/user/admin/users/${item.creator_user_id}/status`, { status: 0 })
        await dismissReports()
        removeReport(item)

      } else if (kind === 'deleteContent') {
        if (item.type === 'comment') await api.delete(`/comments/admin/reel/${item.target_id}`)
        else if (item.type === 'reel') await api.delete(`/reels/admin/reels/${item.target_id}`)
        await dismissReports()
        removeReport(item)

      } else if (kind === 'hideClip') {
        const newStatus = parseInt(item.content_status) === 0 ? 1 : 0
        await api.patch(`/reels/admin/reels/${item.target_id}/status`, { status: newStatus })
        setReports(prev => prev.map(r =>
          r.type === item.type && String(r.target_id) === String(item.target_id)
            ? { ...r, content_status: newStatus }
            : r
        ))

      } else if (kind === 'endStream') {
        await api.post(`/live-history/admin/streams/${item.target_id}/end`).catch(() => {})
        await dismissReports()
        removeReport(item)

      } else if (kind === 'deleteAndSuspend') {
        if (item.type === 'comment') await api.delete(`/comments/admin/reel/${item.target_id}`)
        else if (item.type === 'reel') await api.delete(`/reels/admin/reels/${item.target_id}`)
        await suspendUser()
        await dismissReports()
        removeReport(item)

      // ── Flagged-comments tab ──
      } else if (kind === 'unflag') {
        await api.patch(`/comments/admin/${item.type}/${item.id}/flag`, { flagged: false })
        setComments(prev => prev.filter(c => c.id !== item.id))
        setCommTotal(t => t - 1)

      } else if (kind === 'deleteComment') {
        await api.delete(`/comments/admin/${item.type}/${item.id}`)
        setComments(prev => prev.filter(c => c.id !== item.id))
        setCommTotal(t => t - 1)

      // ── Clips tab ──
      } else if (kind === 'deleteClip') {
        await api.delete(`/reels/admin/reels/${item.reel_id}`)
        setClips(prev => prev.filter(c => c.reel_id !== item.reel_id))
        setClipTotal(t => t - 1)
      }

      setModal(null)
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed')
    } finally { setModalLoad(false) }
  }

  // ── Flagged-comments tab helpers ──
  const handleUnflag = (item) => {
    setModal({ kind: 'unflag', item, title: 'Unflag Comment?', message: 'This comment will be removed from the flagged list.', confirmLabel: 'Yes, Unflag', danger: false })
  }
  const handleDeleteComment = (item) => {
    setModal({ kind: 'deleteComment', item, title: 'Delete Comment?', message: 'This comment will be permanently deleted.', confirmLabel: 'Yes, Delete', danger: true })
  }

  // ── Clip preview confirm ──
  const confirmClipPreview = async () => {
    if (!clipPreview) return
    setPreviewLoad(true)
    try {
      const { item, pendingAction } = clipPreview
      const dismissReports = () =>
        api.delete(`/comments/admin/reports/${item.type || 'reel'}/${item.target_id}`).catch(() => {})

      if (pendingAction === 'hide') {
        if (item.type === 'video') {
          const isRejected = item.status === 'rejected'
          if (isRejected) await api.post(`/videos/admin/${item.video_id}/approve`)
          else await api.post(`/videos/admin/${item.video_id}/reject`, { admin_note: 'Taken down following user report(s)' })
          setVideos(prev => prev.map(v => v.video_id === item.video_id ? { ...v, status: isRejected ? 'ready' : 'rejected' } : v))
        } else {
          const newStatus = item.status === 1 ? 0 : 1
          await api.patch(`/reels/admin/reels/${item.reel_id}/status`, { status: newStatus })
          setClips(prev => prev.map(c => c.reel_id === item.reel_id ? { ...c, status: newStatus } : c))
        }

      } else if (pendingAction === 'delete') {
        if (item.type === 'video') {
          await api.delete(`/videos/admin/${item.video_id}`)
          setVideos(prev => prev.filter(v => v.video_id !== item.video_id))
          setVidTotal(t => t - 1)
        } else {
          await api.delete(`/reels/admin/reels/${item.reel_id}`)
          setClips(prev => prev.filter(c => c.reel_id !== item.reel_id))
          setClipTotal(t => t - 1)
        }

      } else if (pendingAction === 'hideClip') {
        if (item.type === 'video') {
          const isRejected = item.content_status === 'rejected'
          if (isRejected) await api.post(`/videos/admin/${item.target_id}/approve`)
          else await api.post(`/videos/admin/${item.target_id}/reject`, { admin_note: 'Taken down following user report(s)' })
          setReports(prev => prev.map(r =>
            r.type === item.type && String(r.target_id) === String(item.target_id)
              ? { ...r, content_status: isRejected ? 'ready' : 'rejected' }
              : r
          ))
        } else {
          const newStatus = parseInt(item.content_status) === 0 ? 1 : 0
          await api.patch(`/reels/admin/reels/${item.target_id}/status`, { status: newStatus })
          setReports(prev => prev.map(r =>
            r.type === item.type && String(r.target_id) === String(item.target_id)
              ? { ...r, content_status: newStatus }
              : r
          ))
        }

      } else if (pendingAction === 'deleteContent') {
        if (item.type === 'video') await api.delete(`/videos/admin/${item.target_id}`)
        else await api.delete(`/reels/admin/reels/${item.target_id}`)
        await dismissReports()
        removeReport(item)

      } else if (pendingAction === 'suspend') {
        await api.patch(`/user/admin/users/${item.creator_user_id}/status`, { status: 0 })
        await dismissReports()
        removeReport(item)

      } else if (pendingAction === 'deleteAndSuspend') {
        if (item.type === 'video') await api.delete(`/videos/admin/${item.target_id}`)
        else await api.delete(`/reels/admin/reels/${item.target_id}`)
        if (item.creator_user_id) {
          await api.patch(`/user/admin/users/${item.creator_user_id}/status`, { status: 0 }).catch(() => {})
        }
        await dismissReports()
        removeReport(item)
      }

      setClipPreview(null)
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed')
    } finally { setPreviewLoad(false) }
  }

  // ── Clips tab helpers ──
  const handleHideClip   = (item) => setClipPreview({ item, pendingAction: 'hide' })
  const handleDeleteClip = (item) => setClipPreview({ item, pendingAction: 'delete' })

  // ── Videos tab helpers ──
  const handleHideVideo   = (item) => setClipPreview({ item, pendingAction: 'hide' })
  const handleDeleteVideo = (item) => setClipPreview({ item, pendingAction: 'delete' })

  const TABS = [
    { id: 'reports', label: '📋 User Reports', count: repTotal,  accent: '#b45309' },
    { id: 'flagged', label: '⚑ Admin Flagged', count: commTotal, accent: '#b91c1c' },
    { id: 'clips',   label: '▶ Clips',          count: clipTotal, accent: '#6d28d9' },
    { id: 'videos',  label: '🎬 Videos',        count: vidTotal,  accent: '#0d9488' },
  ]

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>⚑ Flagged & Reported</div>
        <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Review user-submitted reports and admin-flagged content. Take action to keep the platform safe.</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="User Reports"    value={repStats.total.toLocaleString()}   accent="#b45309" />
        <StatCard label="Comment Reports" value={repStats.comment.toLocaleString()} accent="#0369a1" />
        <StatCard label="Clip Reports"    value={repStats.reel.toLocaleString()}    accent="#b45309" />
        <StatCard label="Video Reports"   value={repStats.video.toLocaleString()}   accent="#0d9488" />
        <StatCard label="Stream Reports"  value={repStats.stream.toLocaleString()}  accent="#b91c1c" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', marginBottom: 20, gap: 4 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{ padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === t.id ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === t.id ? (t.accent || '#e8440a') : 'transparent'}`, marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {t.label}
            {t.count > 0 && (
              <span style={{ background: tab === t.id ? (t.accent || '#e8440a') : '#e9e9e4', color: tab === t.id ? '#fff' : '#6b7280', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10, fontFamily: "'JetBrains Mono',monospace" }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── User Reports Tab ── */}
      {tab === 'reports' && (
        <div>
          {repLoad ? (
            <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading reports…</div>
          ) : reports.length === 0 ? (
            <EmptyState message="No user-submitted reports right now." />
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>{repTotal} reported item{repTotal !== 1 ? 's' : ''}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {reports.map(item => (
                  <ReportCard
                    key={`${item.type}_${item.target_id}`}
                    item={item}
                    onAction={handleReportAction}
                  />
                ))}
              </div>
              <Pagination page={repPage} pages={repPages} onPrev={() => setRepPage(p => p - 1)} onNext={() => setRepPage(p => p + 1)} />
            </>
          )}
        </div>
      )}

      {/* ── Admin Flagged Tab ── */}
      {tab === 'flagged' && (
        <div>
          {commLoad ? (
            <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading flagged comments…</div>
          ) : comments.length === 0 ? (
            <EmptyState message="No admin-flagged comments right now." />
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>{commTotal} flagged comment{commTotal !== 1 ? 's' : ''}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {comments.map(item => (
                  <CommentCard
                    key={`${item.type}_${item.id}`}
                    item={item}
                    onUnflag={handleUnflag}
                    onDelete={handleDeleteComment}
                  />
                ))}
              </div>
              <Pagination page={commPage} pages={commPages} onPrev={() => setCommPage(p => p - 1)} onNext={() => setCommPage(p => p + 1)} />
            </>
          )}
        </div>
      )}

      {/* ── Clips Tab ── */}
      {tab === 'clips' && (
        <div>
          {clipLoad ? (
            <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading reported clips…</div>
          ) : clips.length === 0 ? (
            <EmptyState message="No reported clips right now." />
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>{clipTotal} reported clip{clipTotal !== 1 ? 's' : ''}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {clips.map(item => (
                  <ClipCard
                    key={item.reel_id}
                    item={item}
                    onHide={handleHideClip}
                    onDelete={handleDeleteClip}
                  />
                ))}
              </div>
              <Pagination page={clipPage} pages={clipPages} onPrev={() => setClipPage(p => p - 1)} onNext={() => setClipPage(p => p + 1)} />
            </>
          )}
        </div>
      )}

      {/* ── Videos Tab ── */}
      {tab === 'videos' && (
        <div>
          {vidLoad ? (
            <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading reported videos…</div>
          ) : videos.length === 0 ? (
            <EmptyState message="No reported videos right now." />
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>{vidTotal} reported video{vidTotal !== 1 ? 's' : ''}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {videos.map(item => (
                  <VideoCard
                    key={item.video_id}
                    item={item}
                    onHide={handleHideVideo}
                    onDelete={handleDeleteVideo}
                  />
                ))}
              </div>
              <Pagination page={vidPage} pages={vidPages} onPrev={() => setVidPage(p => p - 1)} onNext={() => setVidPage(p => p + 1)} />
            </>
          )}
        </div>
      )}

      {/* Confirm modal */}
      <ConfirmModal
        open={!!modal}
        title={modal?.title}
        message={modal?.message}
        confirmLabel={modal?.confirmLabel}
        danger={modal?.danger}
        onConfirm={confirmModal}
        onClose={() => setModal(null)}
        loading={modalLoad}
      />

      {/* Clip preview modal */}
      <ClipPreviewModal
        open={!!clipPreview}
        item={clipPreview?.item}
        pendingAction={clipPreview?.pendingAction}
        onConfirm={confirmClipPreview}
        onClose={() => setClipPreview(null)}
        loading={previewLoad}
      />

    </div>
  )
}
